import { useId } from 'react';
import { AgentDot } from '../../AgentDot';
import { MaestroMark } from '../MaestroMark';
import {
  formatFeedTime,
  HOME3_DESTINATION_LABEL_KEY,
  HOME3_SUBJECT_LABEL_KEY,
  type Home3Event,
} from '../../../lib/home3-feed';
import { useTranslation } from '../../../lib/i18n';
import { scheduledDateLabel } from '../../../lib/task-schedule';
import { Badge, ProgressBar, Switch, Typography } from '../ui';
import styles from './Home3Feed.module.css';

/**
 * One feed row: the time, then three stacked lines, then its state — in three columns.
 *
 * **The CATEGORY is named, not drawn.** The row's first line is the destination in words ("Messages"),
 * where it used to be a glyph in a column of its own. A word says which list this row belongs to
 * without the reader having to learn six marks, and it is what the frame draws.
 *
 * **Subject second.** The description is the event itself, in the user's own terms ("A vendor reply
 * needs your decision"); the channel or the agent is the THIRD line, with the Space after it. A row
 * whose subject line read "Email" or "Sam" would say a thing happened and not what, which is the
 * failure this layout is shaped against.
 *
 * **The badge holds its own column at the row's inline end.** It used to sit inline after the last
 * line, which put a row's state at a different x for every row and left the eye no column to scan.
 *
 * **The agent's mark rides the SECOND line**, beside the name it belongs to — it is part of the "how",
 * not of the title. It is the shared `AgentDot` (the same glyph that agent wears on every surface, via
 * common-lib's id hash), not initials.
 *
 * **Each category adds ONE thing of its own beside the badge**: a routine its on/off switch, a goal
 * its progress bar and percent (Figma 20355-24110 / 20355-23223). A message row has no extra control —
 * its title is the last thing said and its second line who said it, on which channel.
 *
 * The whole row opens the event's detail. The hit target is a `<button>` laid OVER the row rather than
 * the row itself, because a routine row also holds a Switch, and a switch inside a button is invalid
 * HTML (and a click on it would open the row). The overlay is not a DS Button: a DS Button would box
 * and fill a list item. Same triage entry as `AgentsIslandQuickViewRow`. Being empty, the overlay takes its
 * accessible name from the row's own text (`aria-labelledby`): the time, the lines, then the state.
 */
export function Home3FeedRow({
  event,
  agentName,
  spaceName,
  showCategory,
  onOpen,
  maestroName,
  onToggle,
}: {
  event: Home3Event;
  /** The acting agent's display name, resolved by the feed from the roster. Empty when unresolved. */
  agentName: string;
  /** The row's Space, resolved from the workspaces the screen reads. Empty when it has none. */
  spaceName: string;
  /**
   * Whether to name the row's category ("Routines", "Tasks", …) on its first line. Only in the ALL
   * view: once a single category is open, every row shares it, so the label is repeated on every row
   * and is dropped (Figma 19848-66034 / request).
   */
  showCategory: boolean;
  onOpen: () => void;
  /** Maestro's display name, for a row no agent is named on. */
  maestroName: string;
  /** Flips a routine row's switch. Only routine rows carry one (`event.toggle`). */
  onToggle?: (next: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const time = formatFeedTime(event.at);
  const agentId = event.subtitle.kind === 'agent' ? event.subtitle.agentId : '';
  // The left half of the second line is NEVER empty. An agent's NAME comes from `/api/my-agents`, a
  // separate read, so while it loads (or after it fails) the row names its kind instead ("Task |
  // Personal") rather than showing a bare dot beside a Space.
  const subject = t(HOME3_SUBJECT_LABEL_KEY[event.destination]);
  const sub = event.subtitle;
  // A Messages row's second line is "who | channel" and names no Space — the thread is with a person,
  // not inside a Space.
  const contact =
    sub.kind === 'contact'
      ? [sub.name || (sub.nameKey ? t(sub.nameKey) : ''), sub.channel].filter(Boolean).join(' | ') || subject
      : '';
  const how =
    sub.kind === 'contact'
      ? contact
      : sub.kind === 'agent'
        ? agentName || subject
        : sub.kind === 'maestro'
          ? maestroName || subject
          : t('home.overview.subject.unassigned');
  const shownSpace = sub.kind === 'contact' ? '' : spaceName;
  const title = event.titleKey ? t(event.titleKey) : event.title;
  const id = useId();

  return (
    <div className={styles.row}>
      <button
        type="button"
        className={styles.hit}
        onClick={onOpen}
        aria-labelledby={[`${id}-time`, `${id}-body`, event.progress !== undefined && `${id}-progress`, `${id}-badge`]
          .filter(Boolean)
          .join(' ')}
      />
      <Typography id={`${id}-time`} variant="p3" color="secondary" component="span" className={styles.time}>
        {time}
      </Typography>
      <span id={`${id}-body`} className={styles.body}>
        {/* The category, then what happened — both Small/SemiBold in the primary ink (`h4`), which is
            the one role at 14/18 on that tier. The category line shows only in the ALL view; inside a
            single category every row repeats it, so it is dropped there. */}
        {showCategory && (
          <Typography variant="h4" color="primary" component="span" className={styles.category}>
            {t(HOME3_DESTINATION_LABEL_KEY[event.destination])}
          </Typography>
        )}
        <Typography variant="h4" color="primary" component="span" className={styles.titleRow}>
          {/* `titleKey` is the one row whose subject has no words of its own — a sent thread whose
              recipient was never resolved. The model cannot translate, so the row does. */}
          <span className={styles.titleText}>{title}</span>
        </Typography>
        {/* "Email | Pizza Milano" — the how, then the Space, joined by the separator the design draws.
            Either half can be absent (an action has no Space; a bare task has no channel), so the
            joiner is only rendered between two present halves rather than leaving a dangling bar. */}
        {(how || shownSpace) && (
          <Typography variant="heading-04" color="secondary" component="span" className={styles.subRow}>
            {agentId ? <AgentDot id={agentId} size={16} /> : null}
            {sub.kind === 'maestro' ? <MaestroMark size={16} /> : null}
            {how ? <span className={styles.how}>{how}</span> : null}
            {how && shownSpace ? <span className={styles.sep} aria-hidden>|</span> : null}
            {shownSpace ? <span className={styles.space}>{shownSpace}</span> : null}
          </Typography>
        )}
      </span>
      {/* Every row wears exactly one badge — there is no badge-less row, because "nothing is said about
          this" is not one of the states. A dated or gated task shows WHEN instead of a status word. */}
      <span className={styles.state}>
        {event.toggle && (
          <Switch
            className={styles.control}
            checked={event.toggle.on}
            onChange={(next) => onToggle?.(next)}
            disabled={!event.toggle.enabled || !onToggle}
            aria-label={t('routines.toggle.label', { name: title })}
          />
        )}
        {event.progress !== undefined && (
          <span className={styles.progress}>
            <ProgressBar progress="success" value={event.progress} decorative className={styles.progressBar} />
            <Typography id={`${id}-progress`} variant="p2" color="primary" component="span" className={styles.percent}>
              {`${event.progress}%`}
            </Typography>
          </span>
        )}
        {event.scheduledAt ? (
          <Badge id={`${id}-badge`} variant="waiting-on-a-reply" size="tiny" label={scheduledDateLabel(event.scheduledAt, Date.now(), i18n.language || undefined)} />
        ) : event.afterTitle ? (
          <Badge id={`${id}-badge`} variant="waiting-on-a-reply" size="tiny" label={t('tasks.status.after', { title: event.afterTitle })} />
        ) : (
          <Badge id={`${id}-badge`} variant={event.badge.variant} size="tiny" label={t(event.badge.labelKey)} />
        )}
      </span>
    </div>
  );
}
